import { useState } from 'react';
import { Area, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartTooltipV2 } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { ChartContainer } from '@Components/ChartContainer';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { Neutral300, Neutral400, Neutral500, Neutral600 } from '@src/utils/colors';
import { DecayRow, HierarchyLevelConst } from '../../dtc-commerce-mmm-types';
import { formatPercentage } from '../../utils/dtc-commerce-mmm-format';
import { ChannelTooltipSubheader } from '../ChannelTooltipSubheader';
import { EXPANDED_CHART_HEIGHT, ExpandableChartName } from './channel-drawer-constants';
import { DecChannelData, DownloadActions } from './channel-drawer-types';
import { axisFontSizeFor } from './channel-drawer-utils';
import { ChannelDrawerExpandableChart } from './ChannelDrawerExpandableChart';

const DECAY_HEIGHT = 180;

interface ChannelDrawerDecayProps {
  data: DecChannelData[];
  focusedNames: Set<string> | null;
  focusedLabel?: string;
  channelColors: Map<string, string>;
  ciPct: number | null;
  download: DownloadActions;
}

interface DecayTooltipPayloadItem {
  value?: number;
  name?: string;
  color?: string;
  payload?: DecayRow & { _label?: string };
}

interface DecayTooltipProps {
  active?: boolean;
  payload?: DecayTooltipPayloadItem[];
  focusedNames: Set<string> | null;
  channelInfoByName: Map<string, DecChannelData>;
}

const DecayTooltip = ({ active, payload, focusedNames, channelInfoByName }: DecayTooltipProps) => {
  if (!active || !payload?.length) return null;
  const week = payload[0]?.payload?.period;
  if (week === undefined) return null;
  const singleFocus = focusedNames !== null && focusedNames.size === 1;
  let entries = payload;
  if (singleFocus && focusedNames !== null) {
    entries = entries.filter(e => Boolean(e.name) && focusedNames.has(e.name as string));
  }
  if (entries.length === 0) return null;

  return (
    <div className='channel-drawer-curve-tooltip-stack'>
      {entries.map(entry => {
        const row = entry.payload;
        if (!row) return null;
        const info = entry.name ? channelInfoByName.get(entry.name) : undefined;
        const label = info?.label ?? row._label ?? entry.name ?? '';
        return (
          <ChartTooltipV2
            key={entry.name ?? label}
            headers={[label]}
            subHeaderLabel={
              <ChannelTooltipSubheader
                channel={info?.channel ?? ''}
                platform={info?.platform ?? ''}
                mediaType={info?.mediaType}
                hierarchyLevel={HierarchyLevelConst.PLATFORM}
              />
            }
            items={[
              {
                label: 'Weeks after spend',
                value: String(week),
                color: entry.color ?? 'transparent'
              },
              {
                label: '% remaining',
                value: formatPercentage(row.spendCarryover * 100, 1),
                color: entry.color ?? 'transparent'
              }
            ]}
          />
        );
      })}
    </div>
  );
};

export const ChannelDrawerDecay = ({
  data,
  focusedNames,
  focusedLabel,
  channelColors,
  ciPct,
  download
}: ChannelDrawerDecayProps) => {
  const [showCI, setShowCI] = useState(false);
  const hasFocus = focusedNames !== null && focusedNames.size > 0;
  const colorFor = (name: string): string => channelColors.get(name) ?? Neutral300;

  if (data.length === 0) return null;

  const singleSubName: string | null = (() => {
    if (data.length === 1) return data[0].name;
    if (hasFocus && focusedNames !== null && focusedNames.size === 1) {
      return Array.from(focusedNames)[0];
    }
    return null;
  })();
  const ciEnabled = singleSubName !== null;
  const showBand = showCI && ciEnabled;

  const subtitle = focusedLabel ? `Showing ${focusedLabel}` : 'Ad effect carryover across weeks';
  const ciToggleLabel =
    ciPct !== null ? `Show ${ciPct}% credible intervals` : 'Show credible intervals';
  const ciBandColor = singleSubName !== null ? colorFor(singleSubName) : Neutral300;
  const ciChannel =
    singleSubName !== null ? (data.find(ch => ch.name === singleSubName) ?? null) : null;
  const ciBandData = ciChannel
    ? ciChannel.rows.map(r => ({
        period: r.period,
        ciBand: [r.ciLoCarryover, r.ciHiCarryover] as [number, number]
      }))
    : [];

  const channelInfoByName = new Map(data.map(ch => [ch.name, ch]));

  return (
    <ChannelDrawerExpandableChart chartName={ExpandableChartName.DECAY}>
      {(isExpanded, expandButton) => (
        <ChartContainer
          className='channel-drawer-decay'
          title={
            <div className='dtc-commerce-mmm-title-stack'>
              <div>Decay</div>
              <div className='dtc-commerce-mmm-title-stack-sub'>{subtitle}</div>
            </div>
          }
          viewRightActions
          rightActions={
            <div className='channel-drawer-section-right-actions'>
              <ToggleSwitch
                label={ciToggleLabel}
                checked={showCI}
                onChange={setShowCI}
                disabled={!ciEnabled}
              />
              <DownloadDropdown
                onClickOptions={[download.onExcel, () => download.onPng(isExpanded)]}
              />
              {expandButton}
            </div>
          }
        >
          <div className='channel-drawer-chart-stack'>
            <ResponsiveContainer
              width='100%'
              height={isExpanded ? EXPANDED_CHART_HEIGHT : DECAY_HEIGHT}
            >
              <ComposedChart margin={{ top: 8, right: 16, bottom: 28, left: 8 }}>
                <XAxis
                  dataKey='period'
                  type='number'
                  domain={[0, 'dataMax']}
                  tick={{ fontSize: axisFontSizeFor(isExpanded), fill: Neutral600 }}
                  label={{
                    value: 'Weeks after spend',
                    position: 'bottom',
                    offset: 8,
                    style: { fill: Neutral600, fontSize: axisFontSizeFor(isExpanded) }
                  }}
                  allowDuplicatedCategory={false}
                  axisLine={{ stroke: Neutral400 }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={v => formatPercentage(v * 100, 0)}
                  tick={{ fontSize: axisFontSizeFor(isExpanded), fill: Neutral600 }}
                  width={48}
                  domain={[0, 1]}
                  axisLine={{ stroke: Neutral400 }}
                  tickLine={false}
                />
                {singleSubName !== null && (
                  <Tooltip
                    content={
                      <DecayTooltip
                        focusedNames={focusedNames}
                        channelInfoByName={channelInfoByName}
                      />
                    }
                    cursor={{ stroke: Neutral500, strokeWidth: 1, strokeDasharray: '4 3' }}
                  />
                )}
                {showBand && (
                  <Area
                    data={ciBandData}
                    type='monotone'
                    dataKey='ciBand'
                    stroke='none'
                    fill={ciBandColor}
                    fillOpacity={0.18}
                    isAnimationActive={false}
                    activeDot={false}
                  />
                )}
                {data.map(ch => {
                  const isFocused =
                    !hasFocus || (focusedNames !== null && focusedNames.has(ch.name));
                  if (hasFocus && !isFocused) return null;
                  return (
                    <Line
                      key={ch.name}
                      data={ch.rows.map(r => ({ ...r, _label: ch.label }))}
                      type='monotone'
                      dataKey='spendCarryover'
                      stroke={colorFor(ch.name)}
                      strokeWidth={hasFocus && isFocused ? 2.5 : 2}
                      dot={false}
                      name={ch.name}
                      isAnimationActive={false}
                      activeDot={singleSubName !== null ? undefined : false}
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
            {data.length > 1 && (
              <div className='channel-drawer-legend'>
                {data.map(ch => {
                  const isFocused = !hasFocus || focusedNames?.has(ch.name);
                  return (
                    <div
                      key={ch.name}
                      className='channel-drawer-legend-item'
                      style={{ opacity: hasFocus && !isFocused ? 0.4 : 1 }}
                    >
                      <span
                        className='channel-drawer-legend-swatch'
                        style={{
                          backgroundColor: hasFocus && !isFocused ? Neutral300 : colorFor(ch.name)
                        }}
                      />
                      {ch.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ChartContainer>
      )}
    </ChannelDrawerExpandableChart>
  );
};
